import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./InspectionOverview.scss";
import DashboardScopeToggle from "../../components/Common/DashboardScopeToggle";
import FirmOverviewDashboard from "./FirmOverviewDashboard";
import { getMyInspectionReviewTasks } from "../../helpers/backend_helper";
import {
  consolidateChecklistReviews,
  needsAction,
  priorityFirst,
  priorityReason,
  actionLabel,
  taskPath,
} from "../InspectionReviews/workPriorities";

export const nextActionMessage = (task) => {
  const name = task?.taskName || "";
  return name.replace(/^(.*?\bquery\s+RQ-\d+):[\s\S]*$/i, "$1");
};

const complete = (row) => /complete|approved/i.test(row.status || "");
const signoff = (row) =>
  /review|approval|sign.off/i.test(row.status || "") && !complete(row);
const overdue = (row) =>
  !complete(row) &&
  row.dueDate &&
  new Date(row.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
const pct = (row) => Math.max(0, Math.min(100, Number(row.percentage) || 0));
const initials = (name) =>
  (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
const date = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      })
    : "Not scheduled";
const age = (value) =>
  Math.max(
    0,
    Math.floor((Date.now() - new Date(value || Date.now())) / 86400000)
  );
const Icon = ({ name }) => <i className={`bx ${name}`} aria-hidden="true" />;
const Bar = ({ value, tone = "blue" }) => (
  <span className={`inspection-overview__bar ${tone}`}>
    <span style={{ width: `${value}%` }} />
  </span>
);
const Ring = ({ value }) => (
  <div
    className="inspection-overview__ring"
    style={{ "--progress": `${value}%` }}
  >
    <strong>{value}%</strong>
  </div>
);
const Empty = ({ children = "No items to display." }) => (
  <p className="inspection-overview__empty">{children}</p>
);
const Panel = ({
  title,
  action,
  onAction,
  children,
  className = "",
  collapsible = false,
}) => {
  const content = (
    <>
      {action && (
        <button
          type="button"
          className="inspection-overview__link"
          onClick={onAction}
        >
          {action} <Icon name="bx-right-arrow-alt" />
        </button>
      )}
      {children}
    </>
  );
  return collapsible ? (
    <details
      className={`inspection-overview__panel inspection-overview__disclosure ${className}`}
    >
      <summary>{title}</summary>
      <div className="inspection-overview__disclosure-body">{content}</div>
    </details>
  ) : (
    <section className={`inspection-overview__panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {action && (
          <button
            type="button"
            className="inspection-overview__link"
            onClick={onAction}
          >
            {action} <Icon name="bx-right-arrow-alt" />
          </button>
        )}
      </header>
      {children}
    </section>
  );
};

export default function InspectionOverview({ data, loading, error, onRetry }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState("my");
  const [cycle, setCycle] = useState("all");
  const [reviewer, setReviewer] = useState("all");
  const [tab, setTab] = useState("progress");
  const [tasks, setTasks] = useState([]);
  const [taskError, setTaskError] = useState("");
  const [tasksLoading, setTasksLoading] = useState(true);
  const [taskRequest, setTaskRequest] = useState(0);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await getMyInspectionReviewTasks();
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to load your priorities."
          );
        if (active) {
          setTasks(response.data || []);
          setTaskError("");
        }
      } catch (reason) {
        if (active)
          setTaskError(reason.message || "Unable to load your priorities.");
      } finally {
        if (active) setTasksLoading(false);
      }
    };
    load();
    const refresh = () => {
      if (document.visibilityState !== "hidden") load();
    };
    window.addEventListener("focus", refresh);
    const interval = window.setInterval(refresh, 60000);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [taskRequest]);
  const firm = scope === "firm";
  const priorities = consolidateChecklistReviews(tasks)
    .filter(
      (task) =>
        needsAction(task) &&
        (cycle === "all" || String(task.inspectionCycleId) === cycle)
    )
    .sort(priorityFirst);
  const next = priorities[0];
  const workspace = data?.workspace;
  const allReviews =
    workspace?.reviews ||
    data?.resources?.reviewers?.flatMap((person) => person.assigned || []) ||
    [];
  const myIds = new Set((data?.myWork?.reviews || []).map((row) => row.id));
  const reviews = allReviews.filter(
    (row) =>
      (firm ||
        (workspace
          ? row.reviewerId === workspace.currentUserId
          : myIds.has(row.id))) &&
      (cycle === "all" || row.cycleIds?.includes(Number(cycle))) &&
      (!firm || reviewer === "all" || String(row.reviewerId) === reviewer)
  );
  const ids = new Set(reviews.map((row) => row.id));
  const queries = (workspace?.queries || []).filter((row) =>
    ids.has(row.reviewId)
  );
  const findings = (workspace?.findings || []).filter((row) =>
    ids.has(row.reviewId)
  );
  const activities = (workspace?.activities || data?.myWork?.activities || [])
    .filter((row) => !row.reviewId || ids.has(row.reviewId))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const active = reviews.filter((row) => !complete(row));
  const done = reviews.filter(complete);
  const late = reviews.filter(overdue);
  const awaiting = reviews.filter(signoff);
  const notStarted = active.filter((row) =>
    /not.started|new/i.test(row.status)
  );
  const completedMonth = done.filter(
    (row) =>
      row.completedOn &&
      new Date(row.completedOn).getMonth() === new Date().getMonth() &&
      new Date(row.completedOn).getFullYear() === new Date().getFullYear()
  );
  const progress = reviews.length
    ? Math.round((done.length / reviews.length) * 100)
    : 0;
  const cycles = workspace?.cycles || [];
  const people = [
    ...new Map(
      allReviews
        .filter((row) => row.reviewerId)
        .map((row) => [row.reviewerId, row.reviewer])
    ).entries(),
  ];
  const workload = [
    ...new Map(
      reviews.map((row) => [
        row.reviewerId || "unassigned",
        row.reviewer || "Unassigned",
      ])
    ).entries(),
  ]
    .map(([id, name]) => ({
      id,
      name,
      active: active.filter((row) => (row.reviewerId || "unassigned") === id)
        .length,
      overdue: late.filter((row) => (row.reviewerId || "unassigned") === id)
        .length,
    }))
    .sort((a, b) => b.active - a.active);
  const selectedRows =
    tab === "progress"
      ? active
      : tab === "overdue"
      ? late
      : tab === "signoff"
      ? awaiting
      : tab === "unassigned"
      ? reviews.filter((row) => !row.reviewerId)
      : reviews.filter((row) =>
          (tab === "queries" ? queries : findings).some(
            (item) => item.reviewId === row.id
          )
        );
  const openReview = (row) => navigate(`/inspectionreview/${row.id}`);
  const openWork = () => navigate("/review-tasks");
  const openCycles = () => navigate("/inspections/list");
  const reviewName = (id) =>
    reviews.find((row) => row.id === id)?.name || "Inspection review";
  const metricRows = !firm
    ? [
        [
          "Needs your action",
          priorities.length,
          "bx-check-square",
          "blue",
          "Ready assignments",
        ],
        [
          "Overdue actions",
          priorities.filter((task) =>
            priorityReason(task).startsWith("Overdue")
          ).length,
          "bx-time-five",
          "red",
          "Needs attention",
        ],
        [
          "Queries needing action",
          priorities.filter((task) => task.queryId).length,
          "bx-message-rounded",
          "amber",
          "Assigned to you",
        ],
        [
          "Waiting on others",
          consolidateChecklistReviews(tasks).filter(
            (task) =>
              String(task.workflowStatus).toLowerCase() === "pending" &&
              (cycle === "all" || String(task.inspectionCycleId) === cycle)
          ).length,
          "bx-time-five",
          "purple",
          "Upcoming workflow steps",
        ],
      ]
    : [
        [
          "Active reviews",
          active.length,
          "bx-file",
          "blue",
          firm ? "Across your team" : "Assigned to you",
        ],
        [
          "Overdue reviews",
          late.length,
          "bx-time-five",
          "red",
          "Needs attention",
        ],
        [
          "Open queries",
          queries.length,
          "bx-message-rounded",
          "amber",
          "Awaiting response",
        ],
        [
          firm ? "Awaiting sign-off" : "Completed this month",
          firm ? awaiting.length : completedMonth.length,
          firm ? "bx-group" : "bx-check-circle",
          firm ? "purple" : "green",
          firm ? "Ready for approval" : "Signed-off reviews",
        ],
      ];
  if (firm)
    return (
      <FirmOverviewDashboard
        data={data}
        loading={loading}
        error={error}
        onRetry={onRetry}
        cycle={cycle}
        onCycleChange={setCycle}
        onMyWork={() => setScope("my")}
        reviewer={reviewer}
        onReviewerChange={setReviewer}
      />
    );
  return (
    <main
      className={(`inspection-overview ${
        firm ? "" : "inspection-overview--personal"
      }`) + " piers-page"}
    >
      <header className="inspection-overview__intro piers-page-header overview-scope-header">
        <div>
          <h1 className="piers-page-title">{firm ? "Firm overview" : "My Work overview"}</h1>
          <p>
            {firm
              ? "Monitor inspection progress and support your team."
              : "Here’s what needs your attention in PIERS."}
          </p>
        </div>
          <DashboardScopeToggle value={scope} onChange={(value) => {
            setScope(value);
            setTab(value === "firm" ? "overdue" : "progress");
          }} />
        <div className="inspection-overview__controls">

          <select
            aria-label="Inspection cycle"
            value={cycle}
            onChange={(event) => setCycle(event.target.value)}
          >
            <option value="all">All cycles</option>
            {cycles.map((row) => (
              <option value={row.id} key={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          {firm && (
            <select
              aria-label="Reviewer"
              value={reviewer}
              onChange={(event) => setReviewer(event.target.value)}
            >
              <option value="all">All reviewers</option>
              {people.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>
      {error ? (
        <div role="alert" className="inspection-overview__error">
          {error}{" "}
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : loading ? (
        <div role="status" className="inspection-overview__empty">
          Loading inspection overview…
        </div>
      ) : (
        <>
          <section className="inspection-overview__hero">
            <div>
              <span className="inspection-overview__eyebrow">
                {firm
                  ? cycles.find((row) => String(row.id) === cycle)?.name ||
                    "YOUR INSPECTION PROGRAM"
                  : "YOUR NEXT ACTION"}
              </span>
              <h2>
                {firm
                  ? "Inspection cycle progress"
                  : tasksLoading
                  ? "Loading your next action..."
                  : taskError
                  ? "Priorities unavailable"
                  : nextActionMessage(next) || "No items need your action"}
              </h2>
              <p>
                {firm
                  ? `${done.length} of ${reviews.length} reviews signed off`
                  : taskError ||
                    (next
                      ? `${
                          next.engagementName || next.inspectionName
                        } - ${priorityReason(next)}`
                      : "Check My Work for waiting items and completed activity.")}
              </p>
              <div className="inspection-overview__hero-stats">
                {firm
                  ? [
                      [done.length, "completed"],
                      [
                        active.length - awaiting.length - notStarted.length,
                        "in progress",
                      ],
                      [awaiting.length, "awaiting sign-off"],
                      [notStarted.length, "not started"],
                    ].map(([value, label]) => (
                      <span key={label}>
                        <b>{value}</b> {label}
                      </span>
                    ))
                  : next && (
                      <>
                        <span>
                          <Icon name="bx-list-check" /> {next.inspectionNo}
                        </span>
                        <span>
                          <Icon name="bx-message-rounded" /> {priorities.length}{" "}
                          items need your action
                        </span>
                      </>
                    )}
              </div>
              {(firm || next || taskError) && (
                <button
                  type="button"
                  onClick={() =>
                    firm
                      ? cycle === "all"
                        ? openCycles()
                        : navigate(`/inspections/${cycle}/preview`)
                      : taskError
                      ? setTaskRequest((value) => value + 1)
                      : navigate(taskPath(next))
                  }
                >
                  {firm
                    ? "View cycle"
                    : taskError
                    ? "Retry priorities"
                    : actionLabel(next)}{" "}
                  <Icon name="bx-right-arrow-alt" />
                </button>
              )}
            </div>
            {firm && <Ring value={progress} />}
          </section>
          {firm && (
            <>
              <div className="inspection-overview__metrics">
                {metricRows.map(([label, value, icon, tone, hint]) => (
                  <button
                    type="button"
                    key={label}
                    className={`inspection-overview__metric ${tone}`}
                    onClick={() => {
                      if (label === "Overdue reviews") setTab("overdue");
                      else if (label === "Open queries") setTab("queries");
                      else if (label === "Awaiting sign-off") setTab("signoff");
                      else openWork();
                    }}
                  >
                    <span className="inspection-overview__icon">
                      <Icon name={icon} />
                    </span>
                    <span>
                      <span>{label}</span>
                      <strong>
                        {!firm && (tasksLoading || taskError) ? "—" : value}
                      </strong>
                      <small>{hint}</small>
                    </span>
                    <Icon name="bx-chevron-right" />
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="inspection-overview__grid">
            {firm ? (
              <Panel
                title="Team workload"
                action="View team"
                onAction={() => navigate("/piers/dashboard/resources")}
              >
                <table>
                  <thead>
                    <tr>
                      <th>Reviewer</th>
                      <th>Active reviews</th>
                      <th>Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workload.slice(0, 4).map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="inspection-overview__avatar">
                            {initials(row.name)}
                          </span>
                          {row.name}
                        </td>
                        <td>{row.active}</td>
                        <td className="inspection-overview__red">
                          {row.overdue}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!workload.length && <Empty />}
              </Panel>
            ) : (
              <Panel
                title="My Progress"
                className="inspection-overview__progress-panel"
              >
                <small>
                  {cycle === "all"
                    ? "All cycles"
                    : cycles.find((row) => String(row.id) === cycle)?.name}{" "}
                  · Assigned reviews
                </small>
                <div className="inspection-overview__completion">
                  <strong>
                    {done.length} of {reviews.length} completed
                  </strong>
                  <b>{progress}%</b>
                </div>
                <progress
                  aria-label="Assigned reviews completed"
                  max="100"
                  value={progress}
                />
                <div className="inspection-overview__breakdown">
                  <span>
                    <b>{notStarted.length}</b>Not started
                  </span>
                  <span>
                    <b>
                      {
                        active.filter(
                          (row) => !/not.started|new/i.test(row.status)
                        ).length
                      }
                    </b>
                    In progress / review
                  </span>
                  <span>
                    <b>{done.length}</b>Completed
                  </span>
                </div>
                <small>
                  Attention counts refer to workflow actions and may overlap
                  reviews.
                </small>
              </Panel>
            )}
            {!firm && (
              <Panel
                title={`Requires My Attention · ${priorities.length} items`}
                action="View all work"
                onAction={openWork}
                className="inspection-overview__attention"
              >
                {taskError ? (
                  <div role="alert">{taskError}</div>
                ) : tasksLoading ? (
                  <Empty>Loading your priorities…</Empty>
                ) : priorities.length ? (
                  <div className="inspection-overview__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Action / Assignment</th>
                          <th>Priority reason</th>
                          <th>Due</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priorities.slice(0, 3).map((task) => (
                          <tr key={task.id}>
                            <td>
                              <strong>{task.taskName}</strong>
                              <small>
                                {task.engagementName} · {task.assignedRole}
                              </small>
                            </td>
                            <td>{priorityReason(task)}</td>
                            <td>{date(task.dueDate)}</td>
                            <td>
                              <button
                                type="button"
                                className="inspection-overview__open"
                                onClick={() => navigate(taskPath(task))}
                              >
                                {actionLabel(task)}{" "}
                                <Icon name="bx-right-arrow-alt" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <small className="inspection-overview__list-count">
                      Showing {Math.min(3, priorities.length)} of{" "}
                      {priorities.length} items
                    </small>
                  </div>
                ) : (
                  <Empty>No items need your action right now.</Empty>
                )}
              </Panel>
            )}
            {!firm && (
              <>
                <div className="inspection-overview__metrics">
                  {metricRows.map(([label, value, icon, tone, hint]) => (
                    <button
                      type="button"
                      key={label}
                      className={`inspection-overview__metric ${tone}`}
                      onClick={() => {
                        if (label === "Overdue reviews") setTab("overdue");
                        else if (label === "Open queries") setTab("queries");
                        else if (label === "Awaiting sign-off")
                          setTab("signoff");
                        else openWork();
                      }}
                    >
                      <span className="inspection-overview__icon">
                        <Icon name={icon} />
                      </span>
                      <span>
                        <span>{label}</span>
                        <strong>
                          {!firm && (tasksLoading || taskError) ? "—" : value}
                        </strong>
                        <small>{hint}</small>
                      </span>
                      <Icon name="bx-chevron-right" />
                    </button>
                  ))}
                </div>
              </>
            )}
            <Panel
              collapsible={!firm}
              title={
                firm
                  ? "Needs management attention"
                  : "In Progress and review context"
              }
            >
              <div className="inspection-overview__tabs">
                {(firm
                  ? [
                      ["overdue", "Overdue", late.length],
                      ["signoff", "Awaiting sign-off", awaiting.length],
                      [
                        "unassigned",
                        "Unassigned",
                        reviews.filter((row) => !row.reviewerId).length,
                      ],
                      ["queries", "Open queries", queries.length],
                    ]
                  : [
                      ["progress", "In progress", active.length],
                      ["overdue", "Overdue", late.length],
                      ["queries", "Open queries", queries.length],
                      ["findings", "Findings", findings.length],
                    ]
                ).map(([value, label, count]) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={tab === value}
                    onClick={() => setTab(value)}
                  >
                    {label} <b>{count}</b>
                  </button>
                ))}
              </div>
              <div className="inspection-overview__table">
                <table>
                  <thead>
                    <tr>
                      <th>Review</th>
                      {firm && <th>Reviewer</th>}
                      <th>Progress</th>
                      <th>Due</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRows.slice(0, 4).map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.number}</strong>
                          <small>
                            {row.name}
                            {!firm && ` · ${row.client}`}
                          </small>
                        </td>
                        {firm && <td>{row.reviewer}</td>}
                        <td>
                          <div className="inspection-overview__progress">
                            <Bar
                              value={pct(row)}
                              tone={overdue(row) ? "red" : "blue"}
                            />
                            <span>{pct(row)}%</span>
                          </div>
                        </td>
                        <td
                          className={
                            overdue(row) ? "inspection-overview__red" : ""
                          }
                        >
                          {date(row.dueDate)}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="inspection-overview__open"
                            onClick={() => openReview(row)}
                          >
                            Open <Icon name="bx-right-arrow-alt" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!selectedRows.length && (
                <Empty>No reviews require attention in this category.</Empty>
              )}
              {selectedRows.length > 4 && (
                <button
                  type="button"
                  className="inspection-overview__link"
                  onClick={openWork}
                >
                  View all work <Icon name="bx-right-arrow-alt" />
                </button>
              )}
            </Panel>
            <Panel
              collapsible={!firm}
              title={firm ? "Findings requiring follow-up" : "Follow-ups"}
              action={firm ? "View all findings" : "View all queries"}
              onAction={openWork}
            >
              {!firm && (
                <p className="inspection-overview__query-age">
                  Query age:{" "}
                  {queries.filter((row) => age(row.createdOn) <= 7).length} open
                  0–7 days ·{" "}
                  {queries.filter((row) => age(row.createdOn) > 7).length} open
                  8+ days
                </p>
              )}
              {(firm ? findings : queries).slice(0, 3).map((row) => (
                <button
                  type="button"
                  className="inspection-overview__followup"
                  key={row.id}
                  onClick={() => openReview({ id: row.reviewId })}
                >
                  <span
                    className={`inspection-overview__icon ${
                      firm ? "red" : row.responseReceivedOn ? "green" : "amber"
                    }`}
                  >
                    <Icon
                      name={
                        firm
                          ? "bx-file"
                          : row.responseReceivedOn
                          ? "bx-check"
                          : "bx-question-mark"
                      }
                    />
                  </span>
                  <span>
                    <strong>{row.title || "Review query"}</strong>
                    <small>{reviewName(row.reviewId)}</small>
                    {!firm && row.description && (
                      <small className="inspection-overview__description">
                        {row.description}
                      </small>
                    )}
                  </span>
                  <em>
                    {firm
                      ? row.status
                      : row.responseReceivedOn
                      ? "Ready to review"
                      : `${age(row.createdOn)} days open`}
                  </em>
                </button>
              ))}
              {!(firm ? findings : queries).length && (
                <Empty>No outstanding {firm ? "findings" : "queries"}.</Empty>
              )}
            </Panel>
            <Panel
              collapsible={!firm}
              title={firm ? "Inspection cycles" : "Cycle snapshot"}
              action="View all cycles"
              onAction={openCycles}
            >
              <div className="inspection-overview__cycles">
                {cycles
                  .filter((row) => cycle === "all" || String(row.id) === cycle)
                  .slice(0, 3)
                  .map((row) => {
                    const linked = reviews.filter((review) =>
                      review.cycleIds?.includes(row.id)
                    );
                    const count = linked.filter(complete).length;
                    const value = linked.length
                      ? Math.round((count / linked.length) * 100)
                      : 0;
                    return (
                      <button
                        type="button"
                        key={row.id}
                        onClick={() =>
                          navigate(`/inspections/${row.id}/preview`)
                        }
                      >
                        <Ring value={value} />
                        <span>
                          <strong>{row.name}</strong>
                          <small>
                            {count} / {linked.length} reviews
                          </small>
                          <Bar value={value} />
                        </span>
                      </button>
                    );
                  })}
              </div>
              {!cycles.length && <Empty>No inspection cycles available.</Empty>}
            </Panel>
            <Panel
              collapsible={!firm}
              title={firm ? "Team activity" : "Recent activity"}
              action="View all activity"
              onAction={openWork}
            >
              {activities.slice(0, 3).map((row, index) => (
                <div
                  className="inspection-overview__activity"
                  key={`${row.date}-${index}`}
                >
                  <span className="inspection-overview__avatar">
                    {initials(row.person)}
                  </span>
                  <span>
                    <strong>{row.title}</strong>
                    <small>{row.person}</small>
                  </span>
                  <time>{row.time || date(row.date)}</time>
                </div>
              ))}
              {!activities.length && <Empty>No recent activity.</Empty>}
            </Panel>
          </div>
        </>
      )}
    </main>
  );
}
